'use client';

import type { GetV1ReplicasByReplicaUuidChatHistoryResponse } from '@/app/client/types.gen';
import useAppErrorStore from '@/hooks/useAppErrorStore';
import { useMessagesToHostScript } from '@/hooks/useIframeMessages';
import useJWT from '@/hooks/useJWT';
import { useScrollToBottom } from '@/hooks/useScrollToBottom';
import { getThreeRandomStringsFromArray } from '@/lib/utils';
import type { Tables } from '@/models/supabase.types';
import AppError from '@/ui/app-error';
import AppWarning from '@/ui/app-warning';
import ChatHistory from '@/ui/chat/history';
import ChatInput from '@/ui/chat/input';
import ChatSuggestions from '@/ui/chat/suggestions';
import { type Message, useChat } from '@ai-sdk/react';
import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import AgentChatMessage from './chat/agent-message';
import Footer from './footer';
import Header from './header';
import OfflineErrorBox from './offline-error-box';
export interface ChatProps {
  v?: string;
  user?: Tables<'users'> | null;
  replica: Tables<'replicas'> & {
    chat_widget_settings: Tables<'chat_widget_settings'> | null;
  };
  inputPrompt?: string;
  userName?: string | null;
  history?: GetV1ReplicasByReplicaUuidChatHistoryResponse['items'];
  ignoreSubscriptions?: boolean;
  className?: string;
  classNameInput?: string;
  primaryBgColor?: string;
  containerRef?: React.RefObject<HTMLDivElement | null>;
  suggestedQuestion?: string | null;
  hideBranding?: boolean;
}
const getLocalTimeString = () => {
  const now = new Date();
  const localTimeString = new Date(now.getTime() - now.getTimezoneOffset() * 60000).toISOString();
  return localTimeString;
};
const Chat = ({
  replica,
  inputPrompt,
  history = [],
  className = '',
  classNameInput = '',
  suggestedQuestion = null,
  hideBranding
}: ChatProps) => {
  const [suggestedQuestions, setSuggestedQuestions] = useState<string[]>(() => getThreeRandomStringsFromArray(replica.suggested_questions));
  const [isOnline, setIsOnline] = useState<boolean>(true);
  const {
    register,
    jwt
  } = useJWT();
  const {
    setError,
    resetError
  } = useAppErrorStore();
  const {
    handleIframeReady
  } = useMessagesToHostScript();
  const suggestedQuestionSent = useRef(false);
  const initialMessages = useMemo((): Message[] => {
    return history.map(message => ({
      id: String(message.id),
      content: message.content,
      role: message.role,
      createdAt: new Date(message.created_at)
    }));
  }, [history]);
  const randomizeSuggestionQuestions = useCallback(() => {
    setSuggestedQuestions(getThreeRandomStringsFromArray(replica.suggested_questions));
  }, [replica.suggested_questions]);
  const {
    input,
    setInput,
    messages,
    status,
    stop,
    handleSubmit,
    handleInputChange,
    append
  } = useChat({
    initialMessages,
    body: {
      date: getLocalTimeString()
    },
    onError(error) {
      setError({
        friendlyError: 'Uh oh, something went wrong!',
        error
      });
    },
    api: `/api/chat/${replica.uuid}/web`
  });
  const isReplicaThinking = status === 'submitted' || status === 'streaming' && messages.at(-1)?.content === '';
  const messageId = useId();
  const isNewChat = messages.length === 0;
  const showSuggestedQuestions = !!replica.suggested_questions?.length && isNewChat && !suggestedQuestion;
  const sendSuggestion = useCallback(async (content: string) => {
    if (suggestedQuestionSent.current) return;
    const token = jwt || (await register());
    append({
      id: messageId,
      content: content,
      role: 'user',
      createdAt: new Date()
    }, {
      body: {
        jwt: token
      }
    });
    suggestedQuestionSent.current = true;
  }, [append, messageId, register, jwt]);
  const lastAssistantMessageRef = useRef<HTMLDivElement>(null);
  const lastUserMessageRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const chatHistoryContainerRef = useRef<HTMLDivElement>(null);
  const scrollToBottom = useScrollToBottom(chatHistoryContainerRef);

  // biome-ignore lint/correctness/useExhaustiveDependencies: Adds additional space on every new message at the bottom of the chat history so user can see the new message.
  useEffect(() => {
    // Store the current element reference, so we can use it in cleanup
    const currentAssistantMsgRef = lastAssistantMessageRef.current;
    if (chatHistoryContainerRef.current && lastUserMessageRef.current && currentAssistantMsgRef && headerRef.current) {
      const headerHeight = headerRef.current.clientHeight;
      const lastUserMessageHeight = lastUserMessageRef.current.clientHeight;
      const chatHistoryContainerHeight = chatHistoryContainerRef.current.clientHeight;

      // we are limiting the prepared space for the last message to 180px
      const maxEmptySpace = 180;

      // we are reserving space for the message which replica is typing
      const lastMessageMinHeight = Math.min(chatHistoryContainerHeight - headerHeight - lastUserMessageHeight, maxEmptySpace);
      currentAssistantMsgRef.style.minHeight = `${lastMessageMinHeight}px`;
      scrollToBottom();
      return () => {
        // Use the stored reference, not the current one which may have changed
        if (currentAssistantMsgRef) {
          currentAssistantMsgRef.style.minHeight = '0px';
        }
      };
    }
  }, [messages.length, scrollToBottom, isReplicaThinking]);
  useEffect(() => {
    // Update network status
    setIsOnline(navigator?.onLine);
    const handleStatusChange = () => {
      setIsOnline(navigator.onLine);
    };

    // Listen to the online status
    window.addEventListener('online', handleStatusChange);

    // Listen to the offline status
    window.addEventListener('offline', handleStatusChange);

    // Specify how to clean up after this effect for performance improvment
    return () => {
      window.removeEventListener('online', handleStatusChange);
      window.removeEventListener('offline', handleStatusChange);
    };
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: sendSuggestion is not stable
  useEffect(() => {
    if (!suggestedQuestion) return;
    sendSuggestion(suggestedQuestion);
  }, [suggestedQuestion]);
  useEffect(() => {
    handleIframeReady();
  }, [handleIframeReady]);
  const replicaAnswering = status === 'submitted' || status === 'streaming';
  return <div className="h-dvh overflow-y-hidden flex flex-col" data-sentry-component="Chat" data-sentry-source-file="chat.tsx">
      <div ref={chatHistoryContainerRef} className="flex flex-col h-full overflow-y-auto bg-background grow relative custom-scrollbar">
        <Header ref={headerRef} replicaImage={replica.profile_image} replicaDescription={replica.short_description} replicaName={replica.name} data-sentry-element="Header" data-sentry-source-file="chat.tsx" />

        <div className="flex flex-col grow px-4 pt-4 w-full h-full">
          <OfflineErrorBox error={!isOnline} data-sentry-element="OfflineErrorBox" data-sentry-source-file="chat.tsx" />

          <div className="relative">
            <AgentChatMessage content={replica.introduction} data-sentry-element="AgentChatMessage" data-sentry-source-file="chat.tsx" />
            {showSuggestedQuestions && <ChatSuggestions suggestedQuestions={suggestedQuestions} getSuggestionQuestions={randomizeSuggestionQuestions} setSuggestedQuestion={sendSuggestion} />}
          </div>

          {messages.length ? <div className="h-full flex flex-col relative">
              <ChatHistory lastAssistantMessageRef={lastAssistantMessageRef} lastUserMessageRef={lastUserMessageRef} messages={messages} isReplicaThinking={isReplicaThinking} />

              <AppError />
              <AppWarning />
            </div> : null}
        </div>
      </div>

      <div className={twMerge('w-full bg-background/80', className)}>
        <ChatInput inputPrompt={inputPrompt} input={input} setInput={setInput} handleInputChange={handleInputChange} handleSubmit={async (e, data) => {
        resetError();
        const token = jwt || (await register());
        handleSubmit(e, {
          ...data,
          body: {
            ...(data?.body || {}),
            jwt: token
          }
        });
      }} replicaAnswering={replicaAnswering} onStop={stop} replicaName={replica.name} appendMessage={append} classNameInput={classNameInput} isDisabled={!isOnline} data-sentry-element="ChatInput" data-sentry-source-file="chat.tsx" />

        <Footer hideBranding={hideBranding} data-sentry-element="Footer" data-sentry-source-file="chat.tsx" />
      </div>
    </div>;
};
export default Chat;